/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import MuiTextField from '@mui/material/TextField'
import {
  ChangeEvent,
  ReactNode,
  Ref,
  useEffect,
  useState,
  KeyboardEvent,
  forwardRef
} from 'react'
import { InputAdornment } from '@mui/material'
import { inputStyles } from './TextField.styles'
import IconButton from '../IconButton/IconButton'

interface TextFieldProps {
  disabled?: boolean
  placeholder?: string
  label?: string
  field?: any
  hasError?: boolean
  focused?: boolean
  helperText?: string
  type?: 'text' | 'password' | 'email' | 'text-area' | 'number'
  multiline?: boolean
  id?: string
  flexGrow?: number
  value?: string | null
  defaultValue?: string | null
  onChange?: (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void | ((value: string) => void)
  onFocus?: (field: string, value: string) => void
  onBlur?: (field: string, value: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  inputRef?: Ref<any>
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  lines?: number
  isTagInput?: boolean
  maxWidth?: string
  minWidth?: string
  onlyInteger?: boolean
}

function sanitizeToPositiveInteger(value: string): string {
  const cleaned = value.replace(/\D+/g, '')

  if (!cleaned) return ''

  const num = Math.max(1, parseInt(cleaned, 10))

  return String(num)
}

const TextField = forwardRef<any, TextFieldProps>((props, ref) => {
  const {
    disabled,
    placeholder,
    label,
    field,
    hasError,
    focused,
    helperText,
    type = 'text',
    multiline = false,
    id,
    flexGrow,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    startAdornment,
    endAdornment,
    lines,
    isTagInput = false,
    maxWidth,
    minWidth,
    onlyInteger = false
  } = props

  const [showPassword, setShowPassword] = useState(false)
  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  useEffect(() => {
    if (field?.value === undefined || field?.value === '') {
      setShowPassword(false)
    }
  }, [field?.value])

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    let val = e.target.value

    if (type === 'number' && onlyInteger) {
      val = sanitizeToPositiveInteger(val)
    }

    if (val && multiline) {
      val = val.charAt(0).toUpperCase() + val.slice(1)
    }

    e.target.value = val

    field?.onChange(e)

    if (onChange) {
      onChange(e)
    }
  }

  return (
    <MuiTextField
      {...field}
      defaultValue={field?.value || defaultValue}
      value={field?.value || value}
      onChange={handleChange}
      id={id}
      size="small"
      variant="outlined"
      placeholder={placeholder}
      sx={{
        ...inputStyles,
        flexGrow,
        width: flexGrow ? '0' : maxWidth || '100%',
        '.MuiInputBase-root': {
          borderTopLeftRadius: isTagInput ? 0 : '0.25rem',
          borderBottomLeftRadius: isTagInput ? 0 : '0.25rem'
        },
        minWidth: minWidth || '0'
      }}
      disabled={disabled}
      autoComplete="off"
      label={isTagInput ? '' : label}
      error={hasError}
      focused={focused}
      type={type === 'password' && showPassword ? 'text' : type}
      helperText={helperText}
      multiline={multiline}
      rows={multiline ? lines || 5 : 1}
      inputMode={type === 'number' ? 'decimal' : undefined}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      inputRef={ref || inputRef}
      slotProps={{
        input: {
          endAdornment:
            type === 'password' ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={toggleShowPassword}
                  icon={
                    !showPassword
                      ? 'las la-eye icon-medium'
                      : 'las la-eye-slash icon-medium'
                  }
                  color="primary"
                />
              </InputAdornment>
            ) : (
              endAdornment || null
            ),
          startAdornment
        }
      }}
    />
  )
})

export default TextField
